import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link');
        setVerifying(false);
        return;
      }

      try {
        await authService.verifyResetToken(token);
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, password);
      navigate('/login', { 
        state: { message: 'Password reset successfully. Please log in with your new password.' }
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '60px',
          maxWidth: '500px',
          textAlign: 'center',
        }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '20px',
            animation: 'spin 1s linear infinite',
          }}>⏳</div>
          <p style={{ fontSize: '18px', color: '#5a6c7d' }}>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: isMobile ? '15px' : '20px',
      }}>
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '20px' : '24px',
          padding: isMobile ? '40px 30px' : '60px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ 
            fontSize: isMobile ? '24px' : '28px', 
            fontWeight: '700', 
            color: '#c00', 
            marginBottom: '15px',
          }}>
            Invalid Reset Link
          </h2>
          <p style={{ 
            fontSize: isMobile ? '15px' : '16px', 
            color: '#5a6c7d', 
            lineHeight: 1.6,
            marginBottom: '30px',
          }}>
            {error || 'This password reset link is invalid or has expired.'}
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            style={{
              width: '100%',
              padding: isMobile ? '14px' : '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '700',
              cursor: 'pointer',
              marginBottom: '15px',
            }}
          >
            Request New Link
          </button>
          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              padding: isMobile ? '14px' : '16px',
              background: 'transparent',
              color: '#667eea',
              border: '2px solid #667eea',
              borderRadius: '12px',
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? '15px' : '20px',
    }}>
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '40px 30px' : '60px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
      }}>
        <div style={{ 
          marginBottom: isMobile ? '35px' : '40px', 
          textAlign: 'center',
        }}>
          <h3 style={{ 
            fontSize: isMobile ? '28px' : '32px', 
            fontWeight: '700', 
            color: '#667eea', 
            marginBottom: '10px',
            margin: 0,
          }}>
            Reset Password
          </h3>
          <p style={{ 
            fontSize: isMobile ? '14px' : '15px', 
            color: '#5a6c7d',
            margin: 0,
            marginTop: '10px',
          }}>
            Enter your new password below
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: isMobile ? '20px' : '25px',
        }}>
          {error && (
            <div style={{
              padding: isMobile ? '14px' : '16px',
              borderRadius: '12px',
              fontSize: '14px',
              backgroundColor: '#fee',
              color: '#c00',
              border: '1px solid #fcc',
            }}>
              {error}
            </div>
          )}
          
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#667eea', 
              marginBottom: '8px',
            }}>
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              autoFocus
              style={{
                width: '100%',
                padding: isMobile ? '14px' : '16px',
                border: '2px solid #e0e7ef',
                borderRadius: '12px',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#667eea', 
              marginBottom: '8px',
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              style={{
                width: '100%',
                padding: isMobile ? '14px' : '16px',
                border: '2px solid #e0e7ef',
                borderRadius: '12px',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              padding: isMobile ? '16px' : '18px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: isMobile ? '16px' : '17px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;